import { Component, ChangeDetectionStrategy, inject, effect, ChangeDetectorRef, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { PromptDialogService } from '../../services/prompt-dialog.service';

@Component({
  selector: 'app-global-prompt-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    <div *ngIf="promptService.state().show"
         class="fixed bottom-16 left-1/2 transform -translate-x-1/2 w-full max-w-md px-4 z-50">
      <div [class]="containerClass" class="rounded-lg p-4 shadow-lg border">
        <div class="text-center">
          <!-- Icon and Title -->
          <div class="flex items-center justify-center mb-2">
            <i [class]="iconClass" [style.color]="iconColor" class="mr-2"></i>
            <h3 class="text-lg font-medium" [style.color]="titleColor">
              {{ promptService.state().config?.title }}
            </h3>
          </div>

          <!-- Message -->
          <p [class]="messageClass" class="text-sm mb-4 whitespace-pre-line">
            {{ promptService.state().config?.message }}
          </p>

          <!-- Input Field -->
          <div class="mb-4 text-left">
            <textarea
              *ngIf="promptService.state().config?.inputType === 'textarea'"
              [(ngModel)]="inputValue"
              [placeholder]="promptService.state().config?.placeholder || ''"
              [maxlength]="promptService.state().config?.maxLength || 500"
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
              rows="4"
              autofocus></textarea>
            <input
              *ngIf="promptService.state().config?.inputType === 'text'"
              type="text"
              [(ngModel)]="inputValue"
              [placeholder]="promptService.state().config?.placeholder || ''"
              [maxlength]="promptService.state().config?.maxLength || 200"
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              autofocus
              (keyup.enter)="onConfirm()" />
            <div *ngIf="promptService.state().config?.required && !inputValue.trim()"
                 class="text-xs text-red-600 mt-1">
              This field is required
            </div>
          </div>

          <!-- Action Buttons -->
          <div class="flex items-center justify-center space-x-3">
            <button
              type="button"
              (click)="onConfirm()"
              [disabled]="promptService.state().config?.required && !inputValue.trim()"
              [class]="confirmButtonClass"
              class="px-4 py-2 text-sm rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
              {{ promptService.state().config?.confirmText }}
            </button>
            <button
              type="button"
              (click)="onCancel()"
              class="px-4 py-2 bg-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-400 transition-colors">
              {{ promptService.state().config?.cancelText }}
            </button>
          </div>
        </div>
      </div>
    </div>
  `,
  styles: [`
    .fixed {
      animation: slideUpIn 0.3s ease-out;
    }

    @keyframes slideUpIn {
      from {
        opacity: 0;
        transform: translate(-50%, 100%);
      }
      to {
        opacity: 1;
        transform: translate(-50%, 0);
      }
    }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class GlobalPromptDialogComponent {
  protected readonly promptService = inject(PromptDialogService);
  private readonly cdr = inject(ChangeDetectorRef);

  inputValue = '';

  constructor() {
    // Trigger change detection when dialog state changes
    effect(() => {
      const state = this.promptService.state();
      if (state.show) {
        this.inputValue = ''; // Reset input when dialog opens
      }
      this.cdr.markForCheck();
    });
  }

  get type(): string {
    return this.promptService.state().config?.type || 'info';
  }

  get containerClass(): string {
    switch (this.type) {
      case 'danger':
        return 'bg-red-50 border-red-200';
      case 'warning':
        return 'bg-amber-50 border-amber-200';
      case 'info':
        return 'bg-primary-50 border-primary-200';
      case 'success':
        return 'bg-green-50 border-green-200';
      default:
        return 'bg-gray-50 border-gray-200';
    }
  }

  get iconClass(): string {
    switch (this.type) {
      case 'danger':
        return 'fas fa-exclamation-triangle text-xl';
      case 'warning':
        return 'fas fa-exclamation-circle text-xl';
      case 'info':
        return 'fas fa-info-circle text-xl';
      case 'success':
        return 'fas fa-check-circle text-xl';
      default:
        return 'fas fa-question-circle text-xl';
    }
  }

  get iconColor(): string {
    switch (this.type) {
      case 'danger':
        return '#dc2626'; // red-600
      case 'warning':
        return '#d97706'; // amber-600
      case 'info':
        return '#2563eb'; // blue-600
      case 'success':
        return '#16a34a'; // green-600
      default:
        return '#4b5563'; // gray-600
    }
  }

  get titleColor(): string {
    switch (this.type) {
      case 'danger':
        return '#991b1b'; // red-800
      case 'warning':
        return '#92400e'; // amber-800
      case 'info':
        return '#1e40af'; // blue-800
      case 'success':
        return '#166534'; // green-800
      default:
        return '#374151'; // gray-700
    }
  }

  get messageClass(): string {
    switch (this.type) {
      case 'danger':
        return 'text-red-700';
      case 'warning':
        return 'text-amber-700';
      case 'info':
        return 'text-primary-700';
      case 'success':
        return 'text-green-700';
      default:
        return 'text-gray-700';
    }
  }

  get confirmButtonClass(): string {
    switch (this.type) {
      case 'danger':
        return 'bg-red-600 text-white hover:bg-red-700 disabled:hover:bg-red-600';
      case 'warning':
        return 'bg-amber-600 text-white hover:bg-amber-700 disabled:hover:bg-amber-600';
      case 'info':
        return 'bg-primary-600 text-white hover:bg-primary-700 disabled:hover:bg-primary-600';
      case 'success':
        return 'bg-green-600 text-white hover:bg-green-700 disabled:hover:bg-green-600';
      default:
        return 'bg-gray-600 text-white hover:bg-gray-700 disabled:hover:bg-gray-600';
    }
  }

  onConfirm(): void {
    const config = this.promptService.state().config;
    if (config?.required && !this.inputValue.trim()) {
      return;
    }
    this.promptService.handleConfirm(this.inputValue);
  }

  onCancel(): void {
    this.promptService.handleCancel();
  }
}
